<template>
  <section style="width: 100%">
    <hr />

    <v-row justify="center" justify-sm="start" no-gutters>
      <!-- Profile Image -->
      <v-col cols="auto">
        <v-avatar
          @click="viewProfile"
          :size="120"
          class="cursor-pointer"
          style="border: 3px solid white;"
        >
          <base-image
            type="u"
            imageType="n"
            :profile-id="profileId"
            :file-name="profile.logo"
            :contain="false"
            :isProfileImage="true"
            :imageAlt="`${$t('profile')} ${profile.name} ${$t('image')}.`"
          ></base-image>
        </v-avatar>
      </v-col>

      <!-- Profile Info -->
      <v-col
        cols="12"
        sm="10"
        class="pa-1"
        :style="[$vuetify.breakpoint.xs ? { textAlign: 'center' } : {}]"
      >
        <span>{{ $t('writtenBy') }}:</span>

        <v-row no-gutters justify="center" justify-sm="space-between">
          <!-- Profile Name -->
          <v-col cols="12" sm="9" :class="$vuetify.breakpoint.xs ? 'mb-2' : ''">
            <span
              @click="viewProfile"
              style="font-weight: bold; font-size: 1.25rem;"
              class="primary--text cursor-pointer"
              >{{ profile.name }}</span
            >
          </v-col>

          <!-- Socials and contact -->
          <v-col
            cols="12"
            sm="auto"
            :class="$vuetify.breakpoint.xs ? 'mb-2' : ''"
          >
            <v-row no-gutters justify="center" justify-sm="end">
              <v-col
                v-for="social in profileSocials"
                :key="social.key"
                class="d-flex"
                cols="auto"
              >
                <a
                  v-if="social.key === 'emailContact'"
                  :href="`mailto:${social.value}`"
                  target="_blank"
                  style="text-decoration: none"
                >
                  <v-avatar
                    :size="25"
                    :class="$vuetify.breakpoint.smAndDown ? 'mr-1' : 'mr-2'"
                    style="background-color: #86CCD3"
                  >
                    <v-icon color="white" :size="19">{{
                      icons.mdiEmailOutline
                    }}</v-icon>
                  </v-avatar>
                </a>

                <a
                  v-else
                  :href="social.value"
                  target="_blank"
                  class="d-inline-block"
                >
                  <v-avatar
                    :size="25"
                    :class="$vuetify.breakpoint.smAndDown ? 'mr-1' : 'mr-2'"
                  >
                    <img :src="social.logo" :alt="social.key" />
                  </v-avatar>
                </a>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row id="profile-description-container" no-gutters>
          <base-truncator
            id="profile-description"
            :text="profileDescription"
            :class-prop="['mb-0']"
            :additionalStyling="{ fontSize: '.875rem' }"
            :line-clamp="3"
            :disable-tooltip="true"
            :applyClamp="!showMoreDescription"
            tag="p"
          />
        </v-row>

        <v-row no-gutters justify="end">
          <span
            v-if="showMoreOption"
            @click="showMoreDescription = !showMoreDescription"
            class="brand--text cursor-pointer"
            >{{ showMoreDescription ? $t('less') : $t('more') }}</span
          >
        </v-row>
      </v-col>
    </v-row>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import { TemplateArrayHelpers } from '@/utilities/mixins/TemplateArrayHelpers';
import BaseImage from '@/components/base_components/BaseImage/BaseImage.vue';
import BaseTruncator from '@/components/base_components/BaseTruncator/BaseTruncator.vue';

import { mdiEmailOutline } from '@mdi/js';
import fbLogo from '@/assets/f_logo_RGB-Blue_58.png';
import twitterLogo from '@/assets/Twitter_Social_Icon_Circle_Color.svg';
import instagramLogo from '@/assets/instaLogo.png';
import linkedInLogo from '@/assets/linkedInLogo.webp';

export default {
  components: {
    BaseImage,
    BaseTruncator
  },

  props: {
    profile: {
      type: Object,
      required: true
    },

    documentDefaultLang: {
      type: String,
      required: true
    }
  },

  data: () => ({
    showMoreDescription: false,
    showMoreOption: true
  }),

  mounted() {
    //get length of container
    const descriptionContainer = document.getElementById(
      'profile-description-container'
    );
    //get element
    const descriptionElement = document.getElementById('profile-description');
    //get character count
    const descCharCount = descriptionElement.innerHTML.length;
    //find width of container
    const containerWidth = descriptionContainer.offsetWidth;

    const charInPx = 0.125;
    //shown characters is width time characters per px times 3 lines before clamping
    const shownCharacters = charInPx * containerWidth * 3;

    //if shown characters are less or equal we do not show option for more
    this.showMoreOption = shownCharacters <= descCharCount;
  },

  mixins: [TemplateArrayHelpers],

  methods: {
    viewProfile() {
      this.$router.push(`/profile/${this.profileId}/${this.defaultLanguage}`);
    },

    stripHtml(html) {
      const tmp = document.createElement('DIV');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    }
  },

  computed: {
    ...mapState(['defaultLanguage']),

    profileId() {
      return this.profile.profileId.replace('#', '');
    },

    logosAndIcons() {
      return {
        emailContact: '',
        facebook: fbLogo,
        twitter: twitterLogo,
        instagram: instagramLogo,
        linkedIn: linkedInLogo
      };
    },

    profileSocials() {
      const socialsList = ['twitter', 'facebook', 'linkedIn', 'instagram'];
      if (!this.profile?.fake) socialsList.push('emailContact');

      const allSocials = this.profile.template[0].fields
        .filter(field => {
          return (
            socialsList.includes(field.basicInfoFieldName) &&
            this.findTranslation(field.value, this.defaultLanguage)
          );
        })
        .map(field => {
          return {
            key: field.basicInfoFieldName,
            value: this.findTranslation(field.value, this.defaultLanguage),
            logo: this.logosAndIcons[field.basicInfoFieldName]
          };
        });

      return allSocials;
    },

    profileDescription() {
      return this.stripHtml(
        this.findTranslationMultipleOptions(
          this.profile.template[0].fields.find(
            field => field.basicInfoFieldName === 'description'
          ).value,
          this.documentDefaultLang,
          this.defaultLanguage
        )
      );
    },

    icons: () => ({ mdiEmailOutline })
  }
};
</script>

<style scoped>
hr {
  background-color: #e5e5e5;
  display: block;
  flex: 1 1 0px;
  max-width: 100%;
  height: 0px;
  max-height: 0px;
  border: solid #e5e5e5;
  border-width: thin 0 0 0;
  transition: inherit;
  margin: 8px 0px;
}
</style>
